import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import type { YearRow } from "@/lib/dividend-calc";

interface Props {
  rows: YearRow[];
  currency: string;
  labels: { portfolio: string; cumulativeDividends: string; year: string };
}

const ResultsChart = ({ rows, currency, labels }: Props) => {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const fmtFull = (v: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(v);

  const fmtCompact = (v: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency,
      maximumFractionDigits: 1,
      notation: "compact",
    }).format(v);

  const data = rows.map((r) => ({
    year: r.year,
    portfolio: Math.round(r.portfolioValue),
    cumulative: Math.round(r.cumulativeNetDividends),
  }));

  // Downsample tick labels when the horizon is long, keeps axis readable.
  const tickInterval = rows.length > 60 ? 4 : rows.length > 30 ? 2 : 0;

  return (
    <div className="w-full h-[380px] sm:h-[440px]">
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 20, right: 16, left: 0, bottom: 8 }}>
          <defs>
            <linearGradient id="gPortfolio" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.55} />
              <stop offset="60%" stopColor="hsl(var(--primary))" stopOpacity={0.15} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gDiv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--brand-amber, 35 90% 55%))" stopOpacity={0.45} />
              <stop offset="60%" stopColor="hsl(var(--brand-amber, 35 90% 55%))" stopOpacity={0.12} />
              <stop offset="100%" stopColor="hsl(var(--brand-amber, 35 90% 55%))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 6" stroke="hsl(var(--border))" vertical={false} />
          <XAxis
            dataKey="year"
            stroke="hsl(var(--muted-foreground))"
            interval={tickInterval || "preserveStartEnd"}
            reversed={isRtl}
            tickFormatter={(v) => `Y${v}`}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fontSize: 12 }}
          />
          <YAxis
            stroke="hsl(var(--muted-foreground))"
            tickFormatter={fmtCompact}
            width={64}
            orientation={isRtl ? "right" : "left"}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            cursor={{ stroke: "hsl(var(--primary))", strokeWidth: 1, strokeDasharray: "4 4" }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const portfolio = payload.find((p) => p.dataKey === "portfolio")?.value as number;
              const cumulative = payload.find((p) => p.dataKey === "cumulative")?.value as number;
              return (
                <div className="rounded-xl border border-border bg-background/95 backdrop-blur px-4 py-3 shadow-xl">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    {labels.year} {label}
                  </div>
                  <div className="flex items-center gap-2 text-sm mb-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-primary" />
                    <span className="text-foreground/70">{labels.portfolio}</span>
                    <span className="ms-auto font-bold tabular-nums">{fmtFull(portfolio)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span
                      className="w-2.5 h-2.5 rounded-sm"
                      style={{ background: "hsl(var(--brand-amber, 35 90% 55%))" }}
                    />
                    <span className="text-foreground/70">{labels.cumulativeDividends}</span>
                    <span className="ms-auto font-bold tabular-nums">{fmtFull(cumulative)}</span>
                  </div>
                </div>
              );
            }}
          />
          <Legend
            verticalAlign="top"
            align={isRtl ? "right" : "left"}
            iconType="circle"
            wrapperStyle={{ paddingBottom: 12, fontSize: 13 }}
          />
          <Area
            type="monotone"
            dataKey="portfolio"
            name={labels.portfolio}
            stroke="hsl(var(--primary))"
            fill="url(#gPortfolio)"
            strokeWidth={2.5}
            activeDot={{ r: 5, strokeWidth: 2, stroke: "hsl(var(--background))" }}
          />
          <Area
            type="monotone"
            dataKey="cumulative"
            name={labels.cumulativeDividends}
            stroke="hsl(var(--brand-amber, 35 90% 55%))"
            fill="url(#gDiv)"
            strokeWidth={2.5}
            activeDot={{ r: 5, strokeWidth: 2, stroke: "hsl(var(--background))" }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ResultsChart;
